import { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ManualChatPanel from "./ManualChatPanel";
import DataChatPanel from "./DataChatPanel";
import { useAtomValue, useSetAtom } from "jotai";
import { AppAtoms } from "../lib/store";
import { isProductionEnv } from "../config/const";

interface AiChatSideBarProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  pageName?: string;
}

function AiChatSideBar({ open, setOpen, pageName }: AiChatSideBarProps) {
  const isSmartPhone = useAtomValue(AppAtoms.isSmartPhone);
  const loginUser = useAtomValue(AppAtoms.loginUser);
  const setAiChatOpenAtom = useSetAtom(AppAtoms.aiChatOpen);
  const [tab, setTab] = useState<"manual" | "data">("manual");
  const [resetKey, setResetKey] = useState(0);
  const showDataTab =
    !isProductionEnv && loginUser?.db_authority !== "トスアップアカウント";

  useEffect(() => {
    setAiChatOpenAtom(open);
    return () => { setAiChatOpenAtom(false); };
  }, [open, setAiChatOpenAtom]);

  if (!open) return null;
  return (
    <Box
      data-testid="ai-chat-sidebar"
      sx={{
        width: isSmartPhone ? "100%" : "var(--chat-w)",
        marginTop: isSmartPhone ? "var(--header-h)" : undefined,
        borderLeft: isSmartPhone ? undefined : "1px solid var(--color-border-light)",
        boxShadow: isSmartPhone ? "var(--shadow-elevated)" : undefined,
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
        position: isSmartPhone ? "fixed" : "static",
        right: isSmartPhone ? 0 : undefined,
        bottom: isSmartPhone ? 0 : undefined,
        flexShrink: isSmartPhone ? undefined : 0,
        alignSelf: isSmartPhone ? undefined : "stretch",
        height: isSmartPhone ? undefined : "100%",
      }}
    >
      <Box
        data-testid="ai-chat-header"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
          borderBottom: "1px solid var(--color-border-light)",
          flexShrink: 0,
        }}
      >
        <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "var(--color-ai)" }}>
          AIサポート
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box
            component="button"
            aria-label="新しい会話"
            onClick={() => setResetKey((k) => k + 1)}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              height: "28px",
              px: "8px",
              border: "1px solid var(--color-border-light)",
              bgcolor: "transparent",
              cursor: "pointer",
              borderRadius: "14px",
              color: "var(--color-sumi-light)",
              fontSize: "11px",
              fontWeight: 500,
              "&:hover": { bgcolor: "var(--color-washi)" },
            }}
          >
            新しい会話
          </Box>
          <Box
            component="button"
            aria-label="閉じる"
            onClick={() => setOpen(false)}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: "28px",
              height: "28px",
              border: "none",
              bgcolor: "transparent",
              cursor: "pointer",
              borderRadius: "50%",
              color: "var(--color-sumi-light)",
              "&:hover": { bgcolor: "var(--color-washi)" },
            }}
          >
            <CloseIcon sx={{ fontSize: "16px" }} />
          </Box>
        </Box>
      </Box>
      {showDataTab && (
        <Box
          data-testid="ai-chat-mode-tabs"
          sx={{
            display: "flex",
            gap: 1,
            px: 2,
            py: 1,
            borderBottom: "1px solid var(--color-border-light, #efefea)",
          }}
        >
          {(["manual", "data"] as const).map((t) => (
            <Box
              key={t}
              component="button"
              onClick={() => setTab(t)}
              sx={{
                fontSize: "11px",
                fontWeight: 600,
                px: "12px",
                py: "4px",
                borderRadius: "999px",
                border: "none",
                cursor: "pointer",
                bgcolor:
                  tab === t
                    ? "var(--color-ai)"
                    : "var(--color-washi)",
                color:
                  tab === t
                    ? "var(--color-white)"
                    : "var(--color-sumi-light)",
              }}
            >
              {t === "manual" ? "操作サポート" : "データ分析"}
            </Box>
          ))}
        </Box>
      )}
      <Box
        sx={{
          flex: 1,
          overflow: "hidden",
          minHeight: 0,
          display: tab === "manual" ? "block" : "none",
        }}
      >
        <ManualChatPanel key={resetKey} pageName={pageName} />
      </Box>
      {showDataTab && (
        <Box
          sx={{
            flex: 1,
            overflow: "hidden",
            minHeight: 0,
            display: tab === "data" ? "block" : "none",
          }}
        >
          <DataChatPanel />
        </Box>
      )}
    </Box>
  );
}

export default AiChatSideBar;
